import { StandardEvents } from '@shopify/events';

let localRegistrationPending;
async function registerCurrentLocalCart() {
  if (localRegistrationPending) return localRegistrationPending;
  localRegistrationPending = (async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cartUrl = new URL(`${root}cart.js`, window.location.origin);
      if (cartUrl.origin !== window.location.origin || !/\/cart\.js$/.test(cartUrl.pathname)) throw new Error();
      const cartResponse = await fetch(cartUrl, { method: 'GET', credentials: 'same-origin',
        cache: 'no-store', redirect: 'error', headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error();
      const cart = await cartResponse.json();
      if (typeof cart.token !== 'string' || !/^[A-Za-z0-9_-]{10,256}\?key=[A-Za-z0-9_-]{8,256}$/.test(cart.token)) throw new Error();
      const response = await fetch(`${root}apps/kaylulu-local-delivery/register`, {
        method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ cartToken: cart.token }),
      });
      if (!response.ok) throw new Error();
      const result = await response.json();
      if (result?.status !== 'REGISTERED_OWNER_CART' || result.productionCheckoutAuthorized !== false
        || result.noPublicRateAuthorization !== true) throw new Error();
      return { status: 'REGISTERED' };
    } catch { return { status: 'UNAVAILABLE' }; }
    finally { localRegistrationPending = null; }
  })();
  return localRegistrationPending;
}

// Display markers are optional merchandising hints, never checkout authority.
// Current address/cart economics and paid-rate validation remain server-side.
function refreshNotice(element) {
  const until = element.dataset.localValidUntilSeconds
    ? Number(element.dataset.localValidUntilSeconds) * 1000
    : Date.parse(element.dataset.localValidUntil ?? '');
  const fresh = Number.isFinite(until) && until > Date.now();
  const state = fresh && element.dataset.available !== 'false' ? element.dataset.localState : 'unknown';
  for (const [selector, show] of [['[data-local-candidate]', state === 'candidate'],
    ['[data-local-excluded]', state === 'excluded'], ['[data-local-neutral]', !['candidate', 'excluded'].includes(state)]]) {
    element.querySelectorAll(selector).forEach(node => { node.hidden = !show; });
  }
}

const normalizeCanadianPostalCode = value => {
  const compact = typeof value === 'string' ? value.toUpperCase().replace(/[\s-]/g, '') : '';
  return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\d[ABCEGHJ-NPRSTV-Z]\d$/.test(compact)
    ? `${compact.slice(0, 3)} ${compact.slice(3)}` : null;
};

// Variant updates can replace either this element or the entire PDP main region
// (combined listings). Keep only the normalized postal code for the matching PDP
// block so the newly rendered selected SKU can request a fresh quote.
const productPostalCodes = new Map();

class KayluluProductDelivery extends HTMLElement {
  #scope; #generation = 0; #quoteGeneration = 0; #timer;
  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#select);
    this.#timer = setInterval(() => refreshNotice(this), 1000);
    this.addEventListener('submit', this.#postalSubmit);
    this.addEventListener('click', this.#postalChange);
    refreshNotice(this);
    queueMicrotask(() => this.#refreshPreservedPostalCode());
  }
  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#select);
    this.removeEventListener('submit', this.#postalSubmit);
    this.removeEventListener('click', this.#postalChange);
    clearInterval(this.#timer); this.#generation++; this.#quoteGeneration++;
  }
  #hide(message) {
    this.querySelector('[data-variant-delivery-info]')?.setAttribute('hidden', '');
    const status = this.querySelector('[data-delivery-loading]');
    if (status) { status.hidden = false; status.textContent = message; }
  }
  #select = async event => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !event.promise) return;
    const generation = ++this.#generation; this.#quoteGeneration++;
    this.#hide('Updating delivery information…');
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#generation) return;
      const fresh = [...(detail?.html?.querySelectorAll('kaylulu-product-delivery') ?? [])]
        .find(node => node.dataset.displayKey === this.dataset.displayKey);
      if (!fresh || !detail.resource?.id || String(detail.resource.id) !== fresh.dataset.variantId
        || (!detail.newProduct && detail.productId && String(detail.productId) !== this.dataset.productId)) {
        this.#hide('See available delivery options at checkout.'); return;
      }
      for (const key of ['productId', 'variantId', 'available', 'localState', 'localValidUntil']) {
        if (fresh.dataset[key] == null) delete this.dataset[key]; else this.dataset[key] = fresh.dataset[key];
      }
      this.replaceChildren(...[...fresh.childNodes].map(node => node.cloneNode(true)));
      refreshNotice(this);
      this.#refreshPreservedPostalCode();
    } catch {
      if (this.isConnected && generation === this.#generation) this.#hide('Delivery information is unavailable. Please try selecting the item again.');
    }
  };
  #postalSubmit = async event => {
    const form = event.target instanceof Element ? event.target.closest('[data-kaylulu-postal-form]') : null;
    if (!form || !this.contains(form)) return;
    event.preventDefault();
    const input = form.querySelector('[data-kaylulu-postal-input]');
    const button = form.querySelector('[data-kaylulu-postal-submit]');
    const error = this.querySelector('[data-kaylulu-postal-error]');
    const postalCode = normalizeCanadianPostalCode(input?.value);
    if (!postalCode) {
      if (error) { error.textContent = 'Enter a valid Canadian postal code.'; error.hidden = false; }
      input?.focus(); return;
    }
    if (input) input.value = postalCode;
    if (this.dataset.displayKey) productPostalCodes.set(this.dataset.displayKey, postalCode);
    if (error) { error.textContent = ''; error.hidden = true; }
    const generation = ++this.#quoteGeneration;
    if (button) { button.disabled = true; button.textContent = 'Checking…'; }
    try {
      const url = new URL(this.dataset.postalQuoteUrl, window.location.origin);
      if (url.origin !== window.location.origin || url.pathname !== '/apps/kaylulu-local-delivery/postal-quote') throw new Error();
      const response = await fetch(url, { method: 'POST', credentials: 'same-origin', redirect: 'error', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ postalCode, variantId: this.dataset.variantId,
          eligibilityVersion: 'PACKAGE_PROFILE_V1' }) });
      if (!response.ok) throw new Error();
      const quote = await response.json();
      if (!this.isConnected || generation !== this.#quoteGeneration) return;
      if (quote?.status === 'INVALID_POSTAL_CODE') {
        if (error) { error.textContent = 'Enter a valid Canadian postal code.'; error.hidden = false; }
        input?.focus(); return;
      }
      const standardFeeSource = this.querySelector('[data-standard-fee-label]');
      const standardFee = standardFeeSource?.dataset.standardFeeLabel;
      const parsedStandardFeeCents = Number(standardFeeSource?.dataset.standardFeeCents);
      const standardFeeCents = Number.isSafeInteger(parsedStandardFeeCents) && parsedStandardFeeCents >= 0
        ? parsedStandardFeeCents : null;
      const validLocal = quote?.status === 'LOCAL' && quote.currency === 'CAD'
        && [4900, 6900, 14900].includes(quote.feeCents) && quote.postalCode === postalCode;
      const itemIneligible = quote?.status === 'LOCAL_ADDRESS_ITEM_INELIGIBLE' && quote.postalCode === postalCode;
      const itemUnknown = quote?.status === 'LOCAL_ADDRESS_ITEM_UNKNOWN' && quote.postalCode === postalCode;
      const extraLargeLocal = validLocal && quote?.handlingClass === 'EXTRA_LARGE_LOCAL'
        && quote?.customerHandlingCents === 4000;
      const validStandard = ['STANDARD', 'LOCAL_ADDRESS_ITEM_INELIGIBLE', 'LOCAL_ADDRESS_ITEM_UNKNOWN'].includes(quote?.status)
        && quote.postalCode === postalCode && standardFee;
      const eligibilityTitle = itemIneligible ? 'Standard Shipping only'
        : itemUnknown ? 'Local Delivery availability will be confirmed at checkout.' : '';
      const eligibilityCopy = itemIneligible
        ? 'This item is not eligible for Local Delivery due to its packaged size or delivery requirements.' : '';
      if (validLocal) {
        const standardCheaper = standardFee && standardFeeCents !== null && standardFeeCents < quote.feeCents;
        this.#showPostalResult({ postalCode, mode: standardCheaper ? 'BOTH' : 'LOCAL',
          localBaseFee: `from $${quote.feeCents / 100} CAD per order`, standardFee,
          eligibilityTitle, eligibilityCopy: '', extraLargeLocal });
      } else this.#showPostalResult({ postalCode, mode: validStandard ? 'STANDARD' : 'FALLBACK',
        standardFee, eligibilityTitle, eligibilityCopy });
    } catch {
      if (this.isConnected && generation === this.#quoteGeneration) {
        const standardFee = this.querySelector('[data-standard-fee-label]')?.dataset.standardFeeLabel;
        this.#showPostalResult({ postalCode, mode: standardFee ? 'STANDARD' : 'FALLBACK', standardFee });
      }
    } finally {
      if (this.isConnected && generation === this.#quoteGeneration && button) { button.disabled = false; button.textContent = 'Check'; }
    }
  };
  #showPostalResult({ postalCode, mode, localBaseFee = '', standardFee = '', eligibilityTitle = '', eligibilityCopy = '', extraLargeLocal = false }) {
    const initial = this.querySelector('[data-kaylulu-postal-default]');
    const result = this.querySelector('[data-kaylulu-postal-result]');
    if (!result) return;
    const typeNode = result.querySelector('[data-kaylulu-postal-result-type]');
    const postalNode = result.querySelector('[data-kaylulu-postal-result-code]');
    const feeNode = result.querySelector('[data-kaylulu-postal-result-fee]');
    const estimateNode = result.querySelector('[data-kaylulu-postal-estimate]');
    const estimateRow = result.querySelector('[data-kaylulu-postal-estimate-row]');
    const extraLargeNotes = result.querySelectorAll('[data-kaylulu-postal-extra-large-note]');
    const eligibilityNote = result.querySelector('[data-kaylulu-postal-eligibility-note]');
    const eligibilityTitleNode = result.querySelector('[data-kaylulu-postal-eligibility-title]');
    const eligibilityCopyNode = result.querySelector('[data-kaylulu-postal-eligibility-copy]');
    const single = result.querySelector('[data-kaylulu-single-result]');
    const options = result.querySelector('[data-kaylulu-delivery-options]');
    const handoff = result.querySelector('[data-kaylulu-handoff]');
    const local = mode === 'LOCAL' || mode === 'BOTH';
    const both = mode === 'BOTH';
    const standard = mode === 'STANDARD';
    if (single) single.hidden = both;
    if (options) options.hidden = !both;
    if (handoff) { handoff.hidden = !local; handoff.open = false; }
    if (typeNode) typeNode.textContent = local ? 'Local Delivery' : standard ? 'Standard Shipping' : 'Delivery';
    if (postalNode) postalNode.textContent = postalCode;
    if (feeNode) feeNode.textContent = local ? localBaseFee
      : standard ? `${standardFee} for this item` : 'Delivery fee will be confirmed at checkout.';
    const estimate = local
      ? 'Order by Monday for this Friday–Saturday; orders placed after Monday are estimated for the following Friday–Saturday.'
      : standard
        ? '3–8 business days.'
        : '';
    if (estimateNode) estimateNode.textContent = estimate;
    if (estimateRow) { estimateRow.hidden = !estimate || both; estimateRow.open = false; }
    extraLargeNotes.forEach(note => { note.hidden = !extraLargeLocal; });
    if (eligibilityTitleNode) eligibilityTitleNode.textContent = eligibilityTitle;
    if (eligibilityCopyNode) {
      eligibilityCopyNode.textContent = eligibilityCopy;
      eligibilityCopyNode.hidden = !eligibilityCopy;
    }
    if (eligibilityNote) eligibilityNote.hidden = !eligibilityTitle;
    const optionsPostal = result.querySelector('[data-kaylulu-options-postal]');
    const standardOptionFee = result.querySelector('[data-kaylulu-standard-option-fee]');
    const localOptionFee = result.querySelector('[data-kaylulu-local-option-fee]');
    if (optionsPostal) optionsPostal.textContent = postalCode;
    if (standardOptionFee) standardOptionFee.textContent = `${standardFee} for this item`;
    if (localOptionFee) localOptionFee.textContent = localBaseFee;
    if (initial) initial.hidden = true;
    result.hidden = false;
  }
  #postalChange = event => {
    const target = event.target instanceof Element ? event.target.closest('[data-kaylulu-postal-change]') : null;
    if (!target || !this.contains(target)) return;
    this.#quoteGeneration++;
    const initial = this.querySelector('[data-kaylulu-postal-default]');
    const result = this.querySelector('[data-kaylulu-postal-result]');
    if (result) result.hidden = true;
    if (initial) initial.hidden = false;
    this.querySelector('[data-kaylulu-postal-input]')?.focus();
  };
  #refreshPreservedPostalCode() {
    if (!this.isConnected) return;
    const displayKey = this.dataset.displayKey;
    const postalCode = displayKey ? productPostalCodes.get(displayKey) : null;
    const input = this.querySelector('[data-kaylulu-postal-input]');
    const form = this.querySelector('[data-kaylulu-postal-form]');
    if (!postalCode || !(input instanceof HTMLInputElement) || !(form instanceof HTMLFormElement)) return;
    input.value = postalCode;
    form.requestSubmit();
  }
}

if (!customElements.get('kaylulu-product-delivery')) customElements.define('kaylulu-product-delivery', KayluluProductDelivery);

let cartEligibilityGeneration = 0;
async function refreshCartEligibility() {
  const notices = [...document.querySelectorAll('kaylulu-cart-eligibility')];
  if (!notices.length) return;
  const lines = [...document.querySelectorAll('[data-kaylulu-cart-line][data-variant-id]')];
  const variantIds = [...new Set(lines.map(line => line.dataset.variantId).filter(id => /^\d+$/.test(id ?? '')))];
  if (!variantIds.length) { notices.forEach(notice => { notice.hidden = true; }); return; }
  const generation = ++cartEligibilityGeneration;
  const endpoint = notices[0].dataset.postalQuoteUrl;
  try {
    const url = new URL(endpoint, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== '/apps/kaylulu-local-delivery/postal-quote') throw new Error();
    const results = await Promise.all(variantIds.map(async variantId => {
      const response = await fetch(url, { method: 'POST', credentials: 'same-origin', redirect: 'error', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ postalCode: 'L6H 0S6', variantId,
          eligibilityVersion: 'PACKAGE_PROFILE_V1' }) });
      if (!response.ok) return [variantId, 'unknown'];
      const result = await response.json();
      if (result?.postalCode !== 'L6H 0S6') return [variantId, 'unknown'];
      if (result.status === 'LOCAL_ADDRESS_ITEM_INELIGIBLE') return [variantId, 'ineligible'];
      if (result.status === 'LOCAL') return [variantId, 'eligible'];
      return [variantId, 'unknown'];
    }));
    if (generation !== cartEligibilityGeneration) return;
    const states = new Map(results);
    lines.forEach(line => {
      const marker = line.querySelector('[data-kaylulu-cart-line-ineligible]');
      if (marker) marker.hidden = states.get(line.dataset.variantId) !== 'ineligible';
    });
    const hasIneligible = results.some(([, state]) => state === 'ineligible');
    const fullyEligible = results.length > 0 && results.every(([, state]) => state === 'eligible');
    const hasUnknown = !hasIneligible && results.some(([, state]) => state === 'unknown');
    notices.forEach(notice => {
      notice.querySelector('[data-kaylulu-cart-eligible]').hidden = !fullyEligible;
      notice.querySelector('[data-kaylulu-cart-mixed]').hidden = !hasIneligible;
      notice.querySelector('[data-kaylulu-cart-neutral]').hidden = !hasUnknown;
      notice.hidden = !hasIneligible && !fullyEligible && !hasUnknown;
    });
  } catch {
    if (generation !== cartEligibilityGeneration) return;
    lines.forEach(line => {
      const marker = line.querySelector('[data-kaylulu-cart-line-ineligible]');
      if (marker) marker.hidden = true;
    });
    notices.forEach(notice => { notice.hidden = true; });
  }
}

class KayluluCartEligibility extends HTMLElement {
  connectedCallback() { queueMicrotask(() => void refreshCartEligibility()); }
}
if (!customElements.get('kaylulu-cart-eligibility')) customElements.define('kaylulu-cart-eligibility', KayluluCartEligibility);

// Register proactively so cart accelerated checkout and the standard checkout
// button share the same server-verified cart binding. Failure never blocks the
// separately available Standard Shipping path.
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => void registerCurrentLocalCart(), { once: true });
else void registerCurrentLocalCart();
document.addEventListener(StandardEvents.cartLinesUpdate, () => void registerCurrentLocalCart());
document.addEventListener(StandardEvents.cartDiscountUpdate, () => void registerCurrentLocalCart());
document.addEventListener('submit', async event => {
  const form = event.target instanceof HTMLFormElement ? event.target : null;
  if (!form?.querySelector('[name="checkout"]') || form.dataset.kayluluLocalRegistrationComplete === 'true') return;
  event.preventDefault();
  await registerCurrentLocalCart();
  form.dataset.kayluluLocalRegistrationComplete = 'true';
  form.requestSubmit(event.submitter instanceof HTMLElement ? event.submitter : undefined);
}, true);

let opener;
document.addEventListener('click', event => {
  const target = event.target instanceof Element ? event.target : null;
  const trigger = target?.closest('[data-kaylulu-delivery-details]');
  const dialog = document.getElementById('KayluluDeliveryDetails');
  if (trigger && dialog && typeof dialog.showModal === 'function') {
    event.preventDefault(); opener = trigger;
    if (!dialog.open) dialog.showModal();
    dialog.querySelector('h2')?.focus();
  } else if (target?.closest('[data-kaylulu-delivery-close]') && dialog?.open) dialog.close();
});
document.addEventListener('close', event => {
  if (event.target?.id === 'KayluluDeliveryDetails') { if (opener?.isConnected) opener.focus(); opener = null; }
}, true);
document.addEventListener('keydown', event => {
  if (event.key !== 'Tab') return;
  const dialog = document.getElementById('KayluluDeliveryDetails');
  if (!dialog?.open) return;
  const focusable = [...dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter(node => node.getClientRects().length > 0);
  const first = focusable[0], last = focusable.at(-1);
  if (!first) { event.preventDefault(); dialog.querySelector('h2')?.focus(); return; }
  if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
    event.preventDefault(); last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault(); first.focus();
  }
});
